import React, { useState, useRef } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  ArrowRightIcon,
  LockClosedIcon,
  EnvelopeIcon,
  EyeIcon,
  EyeSlashIcon
} from '@heroicons/react/24/outline';
import { useAuth } from "../../contexts/AuthContext";
import { useToast } from '../../contexts/ToastContext';

const Login = () => {
  const { signIn } = useAuth();
  const showToast = useToast();
  const isSubmittingRef = useRef(false); // ✅ Double submit guard

  const [formData, setFormData] = useState({ email: '', password: '' });
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const handleInputChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleLogin = async (e) => {
    e.preventDefault();

    // ✅ Block double submit
    if (isSubmittingRef.current) return;
    if (!formData.email.trim() || !formData.password) {
      showToast('Please fill in all fields', 'error');
      return;
    }

    isSubmittingRef.current = true; // ✅ Lock immediately
    setIsLoading(true);

    try {
      await signIn({
        email: formData.email.trim(),
        password: formData.password,
      });
      showToast('Welcome back!', 'success');
    } catch (err) {
      const errorMessage = err.response?.data?.message || 'Invalid Credentials';
      showToast(errorMessage, 'error');
    } finally {
      setIsLoading(false);
      isSubmittingRef.current = false; // ✅ Unlock after done
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-[#050505] px-4 selection:bg-indigo-500/30">
      <motion.div 
        initial={{ opacity: 0, y: 10 }} 
        animate={{ opacity: 1, y: 0 }} 
        className="max-w-md w-full bg-[#0a0a0a] border border-white/5 rounded-[2rem] shadow-2xl overflow-hidden"
      >
        <div className="p-8 md:p-10">
          <h2 className="text-3xl font-black text-white italic uppercase tracking-tighter mb-2">
            Login
          </h2>
          <p className="text-gray-500 text-xs font-medium uppercase tracking-widest italic mb-8">
            Identify to continue
          </p>

          <form onSubmit={handleLogin} className="space-y-4">
            <div className="relative group">
              <EnvelopeIcon className="absolute left-4 top-4 h-5 w-5 text-gray-600 group-focus-within:text-indigo-500 transition-colors" />
              <input 
                name="email" 
                type="email" 
                placeholder="Email Address" 
                onChange={handleInputChange} 
                className="w-full pl-12 pr-4 py-4 bg-white/5 border border-white/5 rounded-2xl text-white outline-none focus:border-indigo-500/50 focus:bg-white/[0.08] transition-all text-sm italic" 
                required 
              />
            </div>

            <div className="relative group">
              <LockClosedIcon className="absolute left-4 top-4 h-5 w-5 text-gray-600 group-focus-within:text-indigo-500 transition-colors" />
              <input 
                name="password" 
                type={showPassword ? "text" : "password"} 
                placeholder="Password" 
                onChange={handleInputChange} 
                className="w-full pl-12 pr-12 py-4 bg-white/5 border border-white/5 rounded-2xl text-white outline-none focus:border-indigo-500/50 focus:bg-white/[0.08] transition-all text-sm italic" 
                required 
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-4 top-4 text-gray-600 hover:text-indigo-400 transition-colors"
              >
                {showPassword ? <EyeSlashIcon className="h-5 w-5" /> : <EyeIcon className="h-5 w-5" />}
              </button>
            </div>

            <div className="flex justify-end pr-1">
              <Link to="/forgot-password" className="text-[10px] text-indigo-400 hover:text-white font-black uppercase tracking-widest italic transition-colors">
                Forgot Password?
              </Link>
            </div>

            <button 
              type="submit"
              disabled={isLoading} 
              className="w-full py-4 rounded-2xl font-black text-xs uppercase tracking-[0.2em] text-white bg-indigo-600 hover:bg-indigo-500 flex items-center justify-center transition-all active:scale-95 shadow-xl shadow-indigo-600/10 italic disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isLoading ? 'Processing...' : 'Sign In'}
              {!isLoading && <ArrowRightIcon className="h-4 w-4 ml-2" />}
            </button>
          </form>
        </div>

        <div className="py-6 bg-white/[0.02] border-t border-white/5 text-center px-8">
          <p className="text-gray-500 text-[10px] font-bold italic uppercase tracking-widest">
            Don't have an identity? {' '}
            <Link to="/register" className="text-white font-black hover:text-indigo-400 transition-colors underline underline-offset-4 decoration-indigo-500/50">
              Register
            </Link>
          </p>
        </div>
      </motion.div>
    </div>
  );
};

export default Login;